import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import React from "react";
import axios from "axios";

interface Props {
  onResult: (result: any, ad: any) => void;
  prefill?: {
    brandName?: string;
    vertical?: string;
    adSize?: string;
  };
}

function apiUrl(path: string): string {
  const host = window.location.hostname;
  const port = window.location.port;
  const base = port ? `//${host}:${port}` : `//${host}`;
  return `${base}/copilot/genesys/adeval${path}`;
}

const AD_SIZES = ["300x250", "728x90", "160x600", "300x600", "320x50", "970x250"];

const VERTICALS = [
  "Accountant/Tax Services",
  "Attorney/Legal Services",
  "Auto Dealers",
  "Banks/Loans/Credit Unions",
  "Cannabis Products",
  "Charitable Organizations",
  "Dentists",
  "Electrical Services",
  "Fast Food",
  "Food Products",
  "Furniture Stores",
  "HVAC Services",
  "Health Insurance",
  "Health/Fitness",
  "Home Repair/Hardware",
  "Insurance",
  "Jewelry",
  "Museum",
  "Plumbing/Heating/AC",
  "Real Estate",
  "Restaurants",
  "Retirement/Nursing Homes & Services",
  "Shoes",
  "Sporting Goods/Bicycles",
];

const AdevalUpload = ({ onResult, prefill }: Props) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string>("");
  const [brandName, setBrandName] = React.useState(prefill?.brandName || "");
  const [vertical, setVertical] = React.useState(prefill?.vertical || "");
  const [adSize, setAdSize] = React.useState(prefill?.adSize || "300x250");
  const [scoring, setScoring] = React.useState(false);
  const [classifying, setClassifying] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (prefill?.brandName) setBrandName(prefill.brandName);
    if (prefill?.vertical) setVertical(prefill.vertical);
    if (prefill?.adSize) setAdSize(prefill.adSize);
  }, [prefill]);

  const handleFile = async (f: File) => {
    if (!f.type.startsWith("image/")) {
      setError("Please upload an image file (JPEG, PNG, WebP)");
      return;
    }
    setFile(f);
    setError("");
    const url = URL.createObjectURL(f);
    setPreview(url);

    // Auto-detect ad size from image dimensions
    if (!prefill?.adSize) {
      const img = new Image();
      img.onload = () => {
        const detected = `${img.naturalWidth}x${img.naturalHeight}`;
        // Match to known size, or use detected dimensions directly
        if (AD_SIZES.includes(detected)) {
          setAdSize(detected);
        } else {
          // Check for retina (2x) versions
          const half = `${Math.round(img.naturalWidth / 2)}x${Math.round(img.naturalHeight / 2)}`;
          if (AD_SIZES.includes(half)) {
            setAdSize(half);
          } else {
            setAdSize(detected);
          }
        }
      };
      img.src = url;
    }

    // Auto-classify to infer brand and vertical
    setClassifying(true);
    try {
      const formData = new FormData();
      formData.append("image", f);
      const res = await axios.post(apiUrl("/classify/upload"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const classification = res.data?.classification;
      if (classification) {
        if (classification.brand_name && !prefill?.brandName) {
          setBrandName(classification.brand_name);
        }
        if (classification.vertical && !prefill?.vertical) {
          setVertical(classification.vertical);
        }
      }
    } catch (e) {
      console.warn("Auto-classify failed, user can fill manually", e);
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handlePaste = React.useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const f = items[i].getAsFile();
        if (f) handleFile(f);
        break;
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  const handleSubmit = async () => {
    if (!file) return;
    setScoring(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("brandName", brandName);
      formData.append("vertical", vertical);
      formData.append("adSize", adSize);
      formData.append("model", "gpt-4o");
      formData.append("temperature", "0.2");
      formData.append("refLimit", "5");

      const res = await axios.post(apiUrl("/score/upload"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const ad = {
        campaignName: brandName || "Uploaded Ad",
        bgImageUrl: preview,
        vertical,
        adSize,
      };
      onResult(res.data, ad);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setScoring(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {scoring && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Scoring uploaded ad... this takes 10-20 seconds
          </Typography>
        </Box>
      )}

      {/* Drop zone */}
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "grey.600",
          borderRadius: 2,
          p: 4,
          mb: 2,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: dragOver ? "action.hover" : "transparent",
          transition: "all 0.2s",
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        }}
      >
        {preview ? (
          <Box>
            <img
              src={preview}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {file?.name} ({Math.round((file?.size || 0) / 1024)} KB)
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Drop an ad image here, click to browse, or paste (Ctrl+V)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supports JPEG, PNG, WebP
            </Typography>
          </Box>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
      </Box>

      {/* Form fields */}
      {classifying && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Detecting brand and vertical...
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Brand Name"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Autocomplete
          freeSolo
          options={VERTICALS}
          value={vertical}
          onChange={(_e, val) => setVertical(val || "")}
          onInputChange={(_e, val) => setVertical(val || "")}
          size="small"
          sx={{ minWidth: 250 }}
          renderInput={(params) => (
            <TextField {...params} label="Vertical" placeholder="e.g. Auto Dealers, Restaurants" />
          )}
        />
        <Autocomplete
          freeSolo
          options={AD_SIZES}
          value={adSize}
          onChange={(_e, val) => setAdSize(val || "300x250")}
          onInputChange={(_e, val) => setAdSize(val || "300x250")}
          size="small"
          sx={{ minWidth: 160 }}
          renderInput={(params) => (
            <TextField {...params} label="Ad Size" />
          )}
        />
      </Box>

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!file || scoring || classifying}
        size="large"
      >
        Score Ad
      </Button>
    </Box>
  );
};

export default AdevalUpload;
